console.log('🔴 subscription-selector.js is loading...');

/**
 * Cart Subscription Selector
 * Allows users to switch all cart items between one-time and subscription
 * Uses data attributes from line items for selling plan information
 */

class CartSubscriptionSelector extends HTMLElement {
  constructor() {
    super();
    console.log('CartSubscriptionSelector constructor called');
  }

  connectedCallback() {
    console.log('CartSubscriptionSelector connected to DOM');
    
    // Use event delegation since content is dynamically loaded
    this.addEventListener('change', (e) => {
      console.log('Change event detected on:', e.target);
      if (e.target.matches('input[name="purchase-type"]')) {
        console.log('Radio button changed via delegation, value:', e.target.value);
        this.handlePurchaseTypeChange(e);
      }
    });
    
    // Check current state and set correct radio after a delay
    setTimeout(() => {
      this.setCorrectRadioState();
      
      const radioButtons = this.querySelectorAll('input[name="purchase-type"]');
      console.log('Found radio buttons after timeout:', radioButtons.length);
      
      radioButtons.forEach((radio, index) => {
        console.log(`Radio ${index}:`, radio.value, radio.checked);
        radio.addEventListener('change', (e) => {
          console.log('Direct listener fired for:', e.target.value);
          this.handlePurchaseTypeChange(e);
        });
      });
    }, 500);
  }

  setCorrectRadioState() {
    // Check if any items in cart have active selling plans
    const cartItems = document.querySelectorAll('.mini-cart__navigation li[data-line-key]');
    let hasActiveSubscription = false;
    
    cartItems.forEach(item => {
      const currentSellingPlan = item.getAttribute('data-current-selling-plan');
      if (currentSellingPlan && currentSellingPlan.trim() !== '') {
        hasActiveSubscription = true;
      }
    });
    
    // Set the correct radio button
    const oneTimeRadio = this.querySelector('input[value="one-time"]');
    const subscriptionRadio = this.querySelector('input[value="subscription"]');
    
    if (hasActiveSubscription && subscriptionRadio) {
      subscriptionRadio.checked = true;
      if (oneTimeRadio) oneTimeRadio.checked = false;
      console.log('Set radio to: subscription (items have active selling plans)');
    } else if (oneTimeRadio) {
      oneTimeRadio.checked = true;
      if (subscriptionRadio) subscriptionRadio.checked = false;
      console.log('Set radio to: one-time (no active selling plans)');
    }
  }

  getLineItems() {
    // Get all line items from the cart with their data attributes
    const lineItems = [];
    const cartItems = document.querySelectorAll('.mini-cart__navigation li[data-line-key]');
    
    cartItems.forEach(item => {
      const lineKey = item.getAttribute('data-line-key');
      const productId = item.getAttribute('data-product-id');
      const availableSellingPlan = item.getAttribute('data-available-selling-plan');
      const quantity = item.getAttribute('data-quantity');
      
      // Only include items that have a selling plan available (not empty string)
      if (availableSellingPlan && availableSellingPlan.trim() !== '') {
        lineItems.push({
          key: lineKey,
          productId: productId,
          sellingPlanId: availableSellingPlan,
          quantity: parseInt(quantity)
        });
      }
    });
    
    return lineItems;
  }

  async handlePurchaseTypeChange(e) {
    const purchaseType = e.target.value;
    const isSubscription = purchaseType === 'subscription';
    
    console.log('Purchase type changed to:', purchaseType);
    
    // Show progress and disable checkout
    this.showProgress();
    this.disableCheckout();
    this.classList.add('loading');
    
    try {
      // Get line items from DOM data attributes
      const lineItems = this.getLineItems();
      
      console.log('Found line items:', lineItems);
      
      if (lineItems.length === 0) {
        console.log('No items with selling plans found - cart items do not have subscription options available');
        this.hideProgress();
        this.enableCheckout();
        this.classList.remove('loading');
        
        // Reset to one-time purchase
        const oneTimeRadio = this.querySelector('input[value="one-time"]');
        if (oneTimeRadio) {
          oneTimeRadio.checked = true;
        }
        
        return;
      }
      
      console.log('Updating cart items...');
      
      // Update each item
      await this.updateCartItems(lineItems, isSubscription);
      
      console.log('Cart items updated, refreshing cart...');
      
      // Refresh the cart display and wait for it to complete
      await this.refreshCart();
      
      console.log('Cart refresh complete');
      
      // Wait a bit longer for cart to fully reload before hiding progress
      setTimeout(() => {
        this.hideProgress();
        this.enableCheckout();
        this.classList.remove('loading');
        console.log('Progress hidden, checkout enabled');
      }, 1500);
    } catch (error) {
      console.error('Error updating subscription:', error);
      this.hideProgress();
      this.enableCheckout();
      this.classList.remove('loading');
    }
  }
  
  showProgress() {
    const progress = document.getElementById('subscription-update-progress');
    if (progress) {
      progress.style.display = 'block';
    }
  }
  
  hideProgress() {
    const progress = document.getElementById('subscription-update-progress');
    if (progress) {
      progress.style.display = 'none';
    }
  }
  
  disableCheckout() {
    const checkoutButton = document.getElementById('mini-cart-checkout-button');
    if (checkoutButton) {
      checkoutButton.disabled = true;
    }
  }
  
  enableCheckout() {
    const checkoutButton = document.getElementById('mini-cart-checkout-button');
    if (checkoutButton) {
      checkoutButton.disabled = false;
    }
  }

  async updateCartItems(lineItems, isSubscription) {
    if (!isSubscription) {
      // Removing selling plans - need to remove items and re-add without selling plan
      console.log('Removing selling plans by clearing and re-adding items...');
      
      // Step 1: Remove all items with selling plans
      const updates = {};
      for (const item of lineItems) {
        const variantId = item.key.split(':')[0];
        updates[variantId] = 0; // Set to 0 to remove
      }
      
      console.log('Step 1: Removing items:', updates);
      
      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates: updates })
      });
      
      // Step 2: Re-add items without selling plans
      const itemsToAdd = lineItems.map(item => ({
        id: item.key.split(':')[0], // Variant ID
        quantity: item.quantity
      }));
      
      console.log('Step 2: Re-adding items without selling plans:', itemsToAdd);
      
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: itemsToAdd })
      });
      
      const responseData = await response.json();
      console.log('Cart API response:', responseData);
      
      if (!response.ok) {
        console.error('Cart API error:', responseData);
        throw new Error(`Failed to remove selling plans: ${responseData.message || responseData.description || 'Unknown error'}`);
      }
      
      return;
    }
    
    // Adding selling plans - update items sequentially
    let successCount = 0;
    let errorCount = 0;
    
    for (const item of lineItems) {
      const formData = {
        id: item.key,
        quantity: item.quantity,
        properties: {}, // Preserve properties
        selling_plan: parseInt(item.sellingPlanId)
      };
      
      console.log('Sending to cart/change.js:', formData);
      
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });
        
        const responseData = await response.json();
        console.log('Cart API response:', responseData);
        
        if (!response.ok) {
          console.warn('Cart API error (may recover):', responseData);
          errorCount++;
          // Don't throw - continue with other items as cart often recovers
        } else {
          successCount++;
        }
      } catch (error) {
        console.error('Error updating item (continuing):', error);
        errorCount++;
        // Continue with other items
      }
    }
    
    console.log(`Updated ${successCount} items successfully, ${errorCount} errors (cart will sync on refresh)`);
  }

  async refreshCart() {
    // Trigger cart refresh event to reload the mini-cart section
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      detail: { open: false }
    }));
    
    // Wait a bit for the cart to refresh, then set the correct radio state
    setTimeout(() => {
      this.setCorrectRadioState();
    }, 1000);
  }
}

customElements.define('cart-subscription-selector', CartSubscriptionSelector);

console.log('CartSubscriptionSelector custom element defined');

// Add styles for subscription selector
if (!document.getElementById('subscription-selector-styles')) {
  const style = document.createElement('style');
  style.id = 'subscription-selector-styles';
  style.textContent = `
    .cart-subscription-selector {
      display: block;
      margin: 20px 0;
      padding: 16px;
      background: #f9f9f9;
      border-radius: 8px;
    }
    
    .cart-subscription-selector.loading {
      opacity: 0.6;
      pointer-events: none;
    }
    
    .subscription-selector__header {
      margin-bottom: 12px;
    }
    
    .subscription-selector__title {
      font-size: 16px;
      font-weight: 600;
      margin: 0;
      color: #333;
    }
    
    .subscription-selector__options {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    
    .subscription-option {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 16px;
      background: white;
      border: 2px solid #e0e0e0;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;
      position: relative;
    }
    
    .subscription-option:hover {
      border-color: #6b1c1c;
      box-shadow: 0 2px 8px rgba(107, 28, 28, 0.1);
    }
    
    /* Hide default radio button */
    .subscription-option input[type="radio"] {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
    }
    
    /* Custom radio button */
    .subscription-option::before {
      content: '';
      display: block;
      width: 20px;
      height: 20px;
      min-width: 20px;
      border: 2px solid #d0d0d0;
      border-radius: 50%;
      background: white;
      transition: all 0.2s;
      margin-top: 2px;
    }
    
    /* Checked state */
    .subscription-option:has(input:checked) {
      border-color: #6b1c1c;
      background: #fef9f5;
    }
    
    .subscription-option:has(input:checked)::before {
      border-color: #6b1c1c;
      border-width: 6px;
    }
    
    .subscription-option__content {
      display: flex;
      flex-direction: column;
      gap: 4px;
      flex: 1;
    }
    
    .subscription-option__title {
      font-weight: 600;
      font-size: 14px;
      color: #333;
    }
    
    .subscription-option__description {
      font-size: 13px;
      color: #666;
    }
    
    @media (max-width: 749px) {
      .cart-subscription-selector {
        margin: 16px 0;
        padding: 12px;
      }
      
      .subscription-selector__title {
        font-size: 14px;
      }
      
      .subscription-option__title {
        font-size: 13px;
      }
      
      .subscription-option__description {
        font-size: 12px;
      }
    }
  `;
  document.head.appendChild(style);
}

console.log('🟢 subscription-selector.js fully loaded');

// Delivery Schedule Selector Logic
function populateDeliveryDateOptions(selectEl) {
  if (!selectEl) return;

  const currentValue = selectEl.dataset.currentValue || selectEl.value || '';
  const now = new Date();
  const cutoffToday = new Date(now);
  cutoffToday.setHours(22, 0, 0, 0);

  let startDate = new Date(now);
  startDate.setHours(0, 0, 0, 0);
  if (now < cutoffToday) {
    startDate.setDate(startDate.getDate() + 1);
  } else {
    startDate.setDate(startDate.getDate() + 2);
  }

  selectEl.innerHTML = '<option value="">Select a delivery date</option>';

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  let datesAdded = 0;
  let checkDate = new Date(startDate);

  while (datesAdded < 14) {
    const dow = checkDate.getDay();
    if (dow !== 0) {
      const fullValue = dayNames[dow] + ', ' + checkDate.getDate() + ' ' + monthNames[checkDate.getMonth()] + ' ' + checkDate.getFullYear();
      const displayStr = dayNames[dow] + ', ' + checkDate.getDate() + ' ' + monthNames[checkDate.getMonth()];

      const option = document.createElement('option');
      option.value = fullValue;
      option.textContent = displayStr;
      if (fullValue === currentValue) option.selected = true;
      selectEl.appendChild(option);
      datesAdded++;
    }
    checkDate.setDate(checkDate.getDate() + 1);
  }

  selectEl.dataset.currentValue = selectEl.value;
}

// Pickup Schedule Selector Logic
function populatePickupDateOptions(selectEl) {
  if (!selectEl) return;

  const currentValue = selectEl.dataset.currentValue || selectEl.value || '';
  const now = new Date();
  const cutoffToday = new Date(now);
  cutoffToday.setHours(22, 0, 0, 0);

  let startDate = new Date(now);
  startDate.setHours(0, 0, 0, 0);
  if (now < cutoffToday) {
    startDate.setDate(startDate.getDate() + 1);
  } else {
    startDate.setDate(startDate.getDate() + 2);
  }

  selectEl.innerHTML = '<option value="">Select a pickup date</option>';

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  let datesAdded = 0;
  let checkDate = new Date(startDate);

  while (datesAdded < 14) {
    const dow = checkDate.getDay();
    if (dow !== 0) {
      const fullValue = dayNames[dow] + ', ' + checkDate.getDate() + ' ' + monthNames[checkDate.getMonth()] + ' ' + checkDate.getFullYear();
      const displayStr = dayNames[dow] + ', ' + checkDate.getDate() + ' ' + monthNames[checkDate.getMonth()];

      const option = document.createElement('option');
      option.value = fullValue;
      option.textContent = displayStr;
      if (fullValue === currentValue) option.selected = true;
      selectEl.appendChild(option);
      datesAdded++;
    }
    checkDate.setDate(checkDate.getDate() + 1);
  }

  selectEl.dataset.currentValue = selectEl.value;
}

function initDeliverySchedule() {
  const deliveryTrigger = document.getElementById('select-delivery-trigger');
  const deliveryOverlay = document.getElementById('delivery-overlay');
  const deliveryOverlayClose = document.getElementById('delivery-overlay-close');
  const deliveryDateMini = document.getElementById('delivery-date-mini');
  const deliveryTimeMini = document.getElementById('delivery-time-mini');
  const pickupDateMini = document.getElementById('pickup-date-mini');
  const pickupTimeMini = document.getElementById('pickup-time-mini');
  const ageCheckbox = document.getElementById('age-verification-mini');
  const checkoutButton = document.getElementById('mini-cart-checkout-button');
  const deliveryFieldsMini = document.getElementById('delivery-schedule-fields-mini');
  const orderTypeInputs = deliveryOverlay ? deliveryOverlay.querySelectorAll('input[name="attributes[order-type]"]') : [];
  const deliveryFields = deliveryFieldsMini?.querySelectorAll('.delivery-field');
  const pickupFields = deliveryFieldsMini?.querySelectorAll('.pickup-field');

  if (!deliveryTrigger || !deliveryOverlay) {
    return;
  }

  populateDeliveryDateOptions(deliveryDateMini);
  populatePickupDateOptions(pickupDateMini);

  function getOrderType() {
    const checkedOrderType = deliveryOverlay.querySelector('input[name="attributes[order-type]"]:checked');
    return checkedOrderType ? checkedOrderType.value : 'Delivery';
  }

  function updateCartAttributes(attrs) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch((error) => console.error('Error updating cart attributes:', error));
  }

  function clearDeliveryAttributes() {
    if (deliveryDateMini) {
      deliveryDateMini.value = '';
      deliveryDateMini.dataset.currentValue = '';
    }

    if (deliveryTimeMini) {
      deliveryTimeMini.value = '';
    }
  }

  function clearPickupAttributes() {
    if (pickupDateMini) {
      pickupDateMini.value = '';
      pickupDateMini.dataset.currentValue = '';
    }

    if (pickupTimeMini) {
      pickupTimeMini.value = '';
    }
  }

  function updateTriggerText() {
    if (!deliveryTrigger) return;

    const isPickup = getOrderType() === 'Pickup';
    const dateValue = deliveryDateMini ? deliveryDateMini.value : '';
    const timeValue = deliveryTimeMini ? deliveryTimeMini.value : '';

    if (isPickup) {
      deliveryTrigger.textContent = 'Pickup selected';
      deliveryTrigger.classList.remove('has-selection');
      return;
    }

    if (dateValue && timeValue) {
      deliveryTrigger.innerHTML = 'Delivery selected';
      deliveryTrigger.classList.add('has-selection');
      return;
    }

    deliveryTrigger.textContent = 'Select Delivery / Pickup';
    deliveryTrigger.classList.remove('has-selection');
  }

  function updateDeliveryFieldsVisibility() {
    if (!deliveryFieldsMini) return;
    
    const isPickup = getOrderType() === 'Pickup';
    if (deliveryFields) {
      Array.from(deliveryFields).forEach(el => el.style.display = isPickup ? "none" : "block");
    }
    if (pickupFields) {
      Array.from(pickupFields).forEach(el => el.style.display = isPickup ? "block" : "none");
    }
  }

  function updateCheckoutState() {
    if (!checkoutButton) return;

    const requiresAgeVerification = checkoutButton.hasAttribute('data-requires-age-verification');
    const ageVerified = !requiresAgeVerification || (ageCheckbox && ageCheckbox.checked);
    const isPickup = getOrderType() === 'Pickup';
    const deliveryDateSelected = !deliveryDateMini || deliveryDateMini.value !== '';
    const deliveryTimeSelected = !deliveryTimeMini || deliveryTimeMini.value !== '';
    const scheduleValid = isPickup || (deliveryDateSelected && deliveryTimeSelected);

    checkoutButton.disabled = !(ageVerified && scheduleValid);
  }

  function closeOverlay() {
    deliveryOverlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  if (deliveryTrigger.dataset.deliveryInitialized !== 'true') {
    deliveryTrigger.dataset.deliveryInitialized = 'true';
    deliveryTrigger.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      populateDeliveryDateOptions(deliveryDateMini);
      populatePickupDateOptions(pickupDateMini);
      deliveryOverlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      updateDeliveryFieldsVisibility();
      updateCheckoutState();
    });
  }

  if (deliveryOverlayClose && deliveryOverlayClose.dataset.deliveryInitialized !== 'true') {
    deliveryOverlayClose.dataset.deliveryInitialized = 'true';
    deliveryOverlayClose.addEventListener('click', closeOverlay);
  }

  const backdrop = deliveryOverlay.querySelector('.delivery-overlay__backdrop');
  if (backdrop && backdrop.dataset.deliveryInitialized !== 'true') {
    backdrop.dataset.deliveryInitialized = 'true';
    backdrop.addEventListener('click', closeOverlay);
  }

  if (deliveryOverlay.dataset.escapeListenerInitialized !== 'true') {
    deliveryOverlay.dataset.escapeListenerInitialized = 'true';
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && deliveryOverlay.classList.contains('active')) {
        closeOverlay();
      }
    });
  }

  if (deliveryDateMini && deliveryDateMini.dataset.deliveryInitialized !== 'true') {
    deliveryDateMini.dataset.deliveryInitialized = 'true';
    deliveryDateMini.addEventListener('change', function() {
      this.dataset.currentValue = this.value;
      updateCartAttributes({
        'order-type': getOrderType(),
        'delivery-day': this.value
      });
      updateTriggerText();
      updateCheckoutState();
    });
  }

  if (deliveryTimeMini && deliveryTimeMini.dataset.deliveryInitialized !== 'true') {
    deliveryTimeMini.dataset.deliveryInitialized = 'true';
    deliveryTimeMini.addEventListener('change', function() {
      updateCartAttributes({
        'order-type': getOrderType(),
        'delivery-time': this.value
      });
      updateTriggerText();
      updateCheckoutState();
    });
  }

  if (pickupDateMini && pickupDateMini.dataset.deliveryInitialized !== 'true') {
    pickupDateMini.dataset.deliveryInitialized = 'true';
    pickupDateMini.addEventListener('change', function() {
      this.dataset.currentValue = this.value;
      updateCartAttributes({
        'order-type': getOrderType(),
        'pickup-day': this.value
      });
      updateTriggerText();
      updateCheckoutState();
    });
  }

  if (pickupTimeMini && pickupTimeMini.dataset.deliveryInitialized !== 'true') {
    pickupTimeMini.dataset.deliveryInitialized = 'true';
    pickupTimeMini.addEventListener('change', function() {
      updateCartAttributes({
        'order-type': getOrderType(),
        'pickup-time': this.value
      });
      updateTriggerText();
      updateCheckoutState();
    });
  }

  orderTypeInputs.forEach((input) => {
    if (input.dataset.deliveryInitialized === 'true') return;

    input.dataset.deliveryInitialized = 'true';
    input.addEventListener('change', function() {
      if (this.value === 'Pickup') {
        updateCartAttributes({
          'order-type': this.value,
          'delivery-day': '',
          'delivery-time': '',
        });
        clearDeliveryAttributes();
      } else {
        updateCartAttributes({
          'order-type': this.value,
          'pickup-day': '',
          'pickup-time': '',
        });
        clearPickupAttributes();
      }
      updateDeliveryFieldsVisibility();
      updateTriggerText();
      updateCheckoutState();
    });
  });

  if (ageCheckbox && ageCheckbox.dataset.deliveryInitialized !== 'true') {
    ageCheckbox.dataset.deliveryInitialized = 'true';
    ageCheckbox.addEventListener('change', updateCheckoutState);
  }

  if (checkoutButton && checkoutButton.dataset.deliveryInitialized !== 'true') {
    checkoutButton.dataset.deliveryInitialized = 'true';
    checkoutButton.addEventListener('click', function() {
      if (!checkoutButton.disabled) {
        closeOverlay();
      }
    });
  }

  updateDeliveryFieldsVisibility();
  updateTriggerText();
  updateCheckoutState();
}

// Use MutationObserver to detect when delivery button is added to DOM
const deliveryObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.addedNodes.length) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) {
          // Check if this node or its children contain the delivery trigger
          if (node.id === 'select-delivery-trigger' || 
              (node.querySelector && node.querySelector('#select-delivery-trigger'))) {
            setTimeout(initDeliverySchedule, 100);
            break;
          }
        }
      }
    }
  }
});

// Start observing the document for changes
deliveryObserver.observe(document.body, {
  childList: true,
  subtree: true
});

// Run on initial load
setTimeout(initDeliverySchedule, 1000);

// Re-run when cart refreshes
document.addEventListener('cart:refresh', () => {
  setTimeout(initDeliverySchedule, 800);
});

// Listen for cart open events
document.addEventListener('cart:open', () => {
  setTimeout(initDeliverySchedule, 800);
});
